import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { FormControl, MenuItem, Select, SelectChangeEvent, CircularProgress } from '@mui/material'
import { QUERY_CITY_AREA_LOCATION } from '../../../graphqls/queries'
import { CityStepProps } from '../types'

interface LocationData {
  displayName: string
  icon: string
  iconCopy: string
  id: string
  name: string
  path: string
  maps: {
    area: string
    city: string
    id: string
    lat: number
    level: number
    lng: number
    state: string
  }
}

interface CityAreaLocationResponse {
  queryCityAreaLocation: {
    location: LocationData[]
  }
}

const CityStep: React.FC<CityStepProps> = ({ value, onChange, error }) => {
  const [cities, setCities] = useState<string[]>([])

  // Get token from environment variable
  const token = process.env.REACT_APP_APOLLO_TOKEN || ''

  const {
    data,
    loading,
    error: queryError,
  } = useQuery<CityAreaLocationResponse>(QUERY_CITY_AREA_LOCATION, {
    variables: {
      webChannelEms: 'TRIPALINK',
    },
    context: {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    },
  })

  useEffect(() => {
    if (data?.queryCityAreaLocation?.location) {
      const cityList = data.queryCityAreaLocation.location.map(loc => loc.displayName || loc.name)
      setCities([...cityList, '其他'])
    }
  }, [data])

  useEffect(() => {
    if (queryError) {
      console.error('Failed to fetch cities:', queryError)
      // Fallback to hardcoded cities if API fails
      setCities(['洛杉矶', '尔湾', '纽约', '波士顿', '西雅图', '旧金山', '圣地亚哥', '芝加哥', '费城', '华盛顿', '其他'])
    }
  }, [queryError])

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-bold text-[#1d2129] leading-snug">1. 您期望在哪个城市居住？</h2>
      </div>

      <FormControl fullWidth error={error}>
        <Select
          value={value}
          onChange={handleChange}
          displayEmpty
          disabled={loading}
          sx={{
            borderRadius: '12px',
            backgroundColor: 'white',
            '& fieldset': {
              borderColor: error ? '#ef4444' : '#e0e2ef',
            },
            '&:hover fieldset': {
              borderColor: error ? '#ef4444' : '#050a22',
            },
            '&.Mui-focused fieldset': {
              borderColor: error ? '#ef4444' : '#050a22',
            },
          }}
        >
          <MenuItem value="" disabled>
            <span className="text-gray-400">{loading ? '加载中...' : '请选择城市'}</span>
          </MenuItem>
          {loading ? (
            <MenuItem value="" disabled>
              <div className="flex items-center gap-2">
                <CircularProgress size={16} />
                <span>加载城市列表...</span>
              </div>
            </MenuItem>
          ) : (
            cities.map(city => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {error && !value && <p className="text-red-500 text-sm font-medium">此问题为必填项</p>}
    </div>
  )
}

export default CityStep
